import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './editAddressDialog.module.scss'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import CitySelect from '@/components/_custom/CitySelect/citySelect'
import UserInput from '@/components/_custom/UserInput/userInput'

interface EditAddressDialogProps {
    open: boolean
    handleClose: () => void
    parent?: boolean
    country: string
    handleSetCountry: (country: string) => void
    city: string
    setCity: (city: string) => void
    zip: string
    setZip: (zip: string) => void
    address: string
    setAddress: (address: string) => void
}

const EditAddressDialog: FunctionComponent<EditAddressDialogProps> = ({
    open,
    handleClose,
    parent,
    country,
    handleSetCountry,
    city,
    setCity,
    zip,
    setZip,
    address,
    setAddress
}: EditAddressDialogProps) => {
    const { t } = useTranslation()

    const [tempCountry, setTempCountry] = useState<string>(country)
    const [tempCity, setTempCity] = useState<string>(city)
    const [tempZip, setTempZip] = useState<string>(zip)
    const [tempAddress, setTempAddress] = useState<string>(address)

    const [countrySelected, setCountrySelected] = useState<boolean>(false)
    const [citySelected, setCitySelected] = useState<boolean>(false)
    const [zipSelected, setZipSelected] = useState<boolean>(false)

    const [selectCityManually, setSelectCityManually] = useState<boolean>(false)

    function handleConfirm(): void {
        handleSetCountry(tempCountry)
        setCity(tempCity)
        setZip(tempZip)
        setAddress(tempAddress)

        handleCloseAndReset()
    }

    function handleCloseAndReset(): void {
        handleClose()

        setCountrySelected(false)
        setCitySelected(false)
        setZipSelected(false)
        setSelectCityManually(false)
    }

    return (
        <Dialog open={open}>
            <DialogHeader title={t('editMyAddress')} parent={parent} />
            <DialogBody className={styles.DialogBody}>
                {!countrySelected ? (
                    <>
                        <div className={styles.Container}>
                            <div className={styles.UserInput}>
                                <CountrySelect
                                    country={tempCountry}
                                    setCountry={setTempCountry}
                                    parent={parent}
                                    required
                                />
                            </div>
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                parent={parent}
                                onClick={handleCloseAndReset}
                                className={styles.Button}
                            />
                            <ButtonTextIcon
                                type={'next'}
                                parent={parent}
                                onClick={() => setCountrySelected(true)}
                                className={styles.Button}
                                disabled={tempCountry === ''}
                            />
                        </div>
                    </>
                ) : !citySelected && tempCountry !== '' ? (
                    <>
                        <div className={styles.Container}>
                            {selectCityManually ? (
                                <UserInput
                                    label={t('city')}
                                    value={tempCity}
                                    setValue={setTempCity}
                                    type='text'
                                    required
                                    parent={parent}
                                    fullWidth
                                    className={styles.UserInput}
                                    format={'pascal'}
                                    unallowedCharacters={'num'}
                                />
                            ) : (
                                <div className={styles.Input}>
                                    <div className={styles.UserInput}>
                                        <CitySelect
                                            key={country + tempCountry}
                                            city={tempCity}
                                            setCity={setTempCity}
                                            parent={parent}
                                            country={tempCountry}
                                        />
                                    </div>
                                    <div className={styles.TextContainer}>
                                        <a className={styles.Text} onClick={() => setSelectCityManually(true)}>
                                            {t('myCityWasNotInList')}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                parent={parent}
                                onClick={handleCloseAndReset}
                                className={styles.Button}
                            />
                            <ButtonTextIcon
                                type={'next'}
                                parent={parent}
                                onClick={() => setCitySelected(true)}
                                className={styles.Button}
                                disabled={tempCity === ''}
                            />
                        </div>
                    </>
                ) : !zipSelected && tempCity !== '' ? (
                    <>
                        <div className={styles.Container}>
                            <UserInput
                                label={t('zipCode')}
                                value={tempZip}
                                setValue={setTempZip}
                                type='text'
                                required
                                parent={parent}
                                fullWidth
                                className={styles.UserInput}
                                format={'zipCode'}
                                unallowedCharacters={'alpha'}
                            />
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                parent={parent}
                                onClick={handleCloseAndReset}
                                className={styles.Button}
                            />
                            <ButtonTextIcon
                                type={'next'}
                                parent={parent}
                                onClick={() => setZipSelected(true)}
                                className={styles.Button}
                                disabled={tempZip === ''}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.Container}>
                            <UserInput
                                label={t('address')}
                                value={tempAddress}
                                setValue={setTempAddress}
                                type='text'
                                required
                                parent={parent}
                                fullWidth
                                className={styles.UserInput}
                                unallowedCharacters={'special'}
                            />
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                parent={parent}
                                onClick={handleCloseAndReset}
                                className={styles.Button}
                            />
                            <ButtonTextIcon
                                type={'confirm'}
                                parent={parent}
                                onClick={handleConfirm}
                                className={styles.Button}
                                disabled={tempAddress === ''}
                            />
                        </div>
                    </>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default EditAddressDialog
